import { ICore, useProfiles, useStudentMe } from "@kunduz/data-access-core-api";
import React, {
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
} from "react";
import { useUserId } from "@kunduz/utility-react-hooks";
import {
  useToast,
  ToastOptions,
  useLocale,
  ILocaleInfo,
  useUserLanguage,
  useProfileLanguage,
  useGtagMiddleware,
} from "@kunduz/feature-website-common";
import { setUser, setTag } from "@sentry/nextjs";
import { identifyStudent, PageviewEvents } from "@kunduz/utility-analytics";
import { IAuth } from "@kunduz/data-access-auth-api";
import { useLocalStorage } from "react-storage-complete";
import { useLogout } from "./useLogout";
import { useRouter } from "next/router";
import { getCookies, getCookie } from "cookies-next";
import { eventBus, SiteLangEnum } from "@kunduz/utility-common";
import { AlertOptions, useAlert } from "@kunduz/ui";
import { useFirebaseRemoteConfig } from "@kunduz/feature-firebase";

export const AppContext = createContext<IAppContext>({} as any);

export const AppContextProvider = (props: PropsWithChildren<ContextProps>) => {
  const [kunduzApiToken, setKunduzApiToken, kunduzApiTokenInitialized] =
    useLocalStorage<string>("kunduzApiToken");
  const [kunduzApiRole, setKunduzApiRole, kunduzApiRoleInitialized] =
    useLocalStorage<string>("kunduzApiRole");
  const {
    refreshStudentExtended,
    studentExtended,
    studentExtendedError,
    studentExtendedLoading,
  } = useStudentMe(kunduzApiToken as string, kunduzApiRole as string);
  const { profiles } = useProfiles(kunduzApiToken as string);
  const {
    countries,
    countriesError,
    countriesLoading,
    locale,
    selectedCountry,
  } = useLocale({ lang: props.lang });
  const { toast, toastError, toastSuccess } = useToast();
  const { AlertComponent, showAlert, hideAlert } = useAlert();
  const { userLang, setUserLanguage } = useUserLanguage(studentExtended);
  // const { getValue, initialized } = useFirebaseRemoteConfig({
  //   enabled: !!selectedCountry,
  //   customSignals: {
  //     custom_signal_language: selectedCountry?.iso_code?.startsWith("TR")
  //       ? "TR"
  //       : "EN",
  //   },
  // });
  useProfileLanguage(profiles, setUserLanguage);
  const { logoutFn } = useLogout();
  const router = useRouter();
  useUserId(studentExtended?.user_id, !!kunduzApiToken);
  useGtagMiddleware(profiles);

  useEffect(() => {
    const user_id = getCookie("kz_user_id");

    PageviewEvents.sendPageView({ user_id: user_id as string });
  }, [router.asPath]);

  useEffect(() => {
    if (!kunduzApiTokenInitialized) return;
    async function handleCookieSync() {
      const cookies = await getCookies();
      if (cookies["kz_user_token"]) {
        setKunduzApiToken(cookies["kz_user_token"]);
      }
    }
    handleCookieSync();
  }, [kunduzApiTokenInitialized]);

  useEffect(() => {
    if (!kunduzApiRoleInitialized) return;
    async function handleCookieSync() {
      const cookies = await getCookies();
      if (cookies["kz_profile"]) {
        setKunduzApiRole(cookies["kz_profile"]);
      }
    }
    handleCookieSync();
  }, [kunduzApiRoleInitialized]);

  /**
   * handler for user_banned event
   * logout user when user_banned received
   */
  useEffect(() => {
    function onUserBanned() {
      logoutFn().then(() => {
        window.location.reload();
      });
    }

    eventBus.on("user_banned", onUserBanned);

    return () => {
      eventBus.off("user_banned", onUserBanned);
    };
  }, []);

  useEffect(() => {
    try {
      setTag("authenticated", false);
      setTag("user-agent", window.navigator.userAgent);
    } catch (error) {
      /* empty */
    }
  }, []);

  useEffect(() => {
    if (!studentExtended) return;
    // identify user to sentry
    setUser({
      email: studentExtended.email,
      username: `${studentExtended.first_name} ${studentExtended.last_name}`,
      id: studentExtended.id.toString(),
      segment: "student",
      phone: studentExtended.phone_number?.number,
    });
    setTag("authenticated", true);

    // identify user to kunduzlytics
    identifyStudent(studentExtended);
  }, [studentExtended?.id]);

  return (
    <AppContext.Provider
      value={{
        student: {
          data: studentExtended,
          loading: studentExtendedLoading,
          error: studentExtendedError,
          refresh: refreshStudentExtended,
        },
        profiles,
        kunduzApiToken,
        localeInfo: {
          countries,
          countriesError,
          countriesLoading,
          locale,
          selectedCountry,
        },
        toast: {
          toast,
          toastError,
          toastSuccess,
        },
        userInfo: {
          userLang,
          setUserLanguage,
        },
        logout: logoutFn,
        showAlert,
        hideAlert,
        // config: {
        //   getValue,
        //   initialized,
        // },
      }}
    >
      <AlertComponent />
      {props.children}
    </AppContext.Provider>
  );
};

export interface IAppContext {
  student: {
    data: ICore.IStudentV4 | undefined;
    loading: boolean;
    error: any;
    refresh: () => Promise<void>;
  };
  profiles: IAuth.IVerifyOtpResponse["profiles"] | null;
  kunduzApiToken?: string | null;
  localeInfo: {
    countries?: ICore.ICountry[];
    countriesError: any;
    countriesLoading: boolean;
    locale?: ILocaleInfo;
    selectedCountry?: ICore.ICountry;
  };
  toast: {
    toast: (title: string | ReactNode, options?: ToastOptions) => void;
    toastError: (title: string | ReactNode, options?: ToastOptions) => void;
    toastSuccess: (title: string | ReactNode, options?: ToastOptions) => void;
  };
  userInfo: {
    userLang?: SiteLangEnum;
    setUserLanguage: (lang: SiteLangEnum) => void;
  };
  logout: () => Promise<void>;

  showAlert: (_options: AlertOptions) => void;
  hideAlert: () => void;
  // config: {
  //   getValue: (key: string) => string;
  //   initialized: boolean;
  // };
}

interface ContextProps {
  lang?: SiteLangEnum;
}

export const useAppContext = () => useContext(AppContext);
